import React, { useEffect, useRef, useState } from 'react';
import { Seo } from './components/Seo';
import { MetaPixel } from './components/MetaPixel';
import { BundleMockup, BonusCover } from './components/BundleMockup';
import { scrollToOffer, goToCheckout } from './lib/checkout';
import { PRICE, ORIGINAL_PRICE, BONUS_IMAGES, SUPPORT_EMAIL, INSTAGRAM_HANDLE, INSTAGRAM_URL } from './data/config';
import { VOLUMES, PAINS, INSIDE, PREVIEW, BONUSES, STEPS, FAQ, Volume } from './data/content';
const BONUS_TOTAL = BONUSES.reduce((s, b) => s + b.value, 0);
const TOTAL_VALUE = ORIGINAL_PRICE + BONUS_TOTAL;
const TEMPLATE_COUNT = VOLUMES.reduce((s, v) => s + v.templates.length, 0);
function Label({
  children,
  light = false
}: {
  children: React.ReactNode;
  light?: boolean;
}) {
  return <p className={`text-[11px] font-bold uppercase tracking-[0.22em] ${light ? 'text-terra' : 'text-sage'}`} data-id="element-0" data-mp="2wi5mfg6wwjo9">{children}</p>;
}

// Scroll-only button (never goes to checkout).
function SeeButton({
  text
}: {
  text: string;
}) {
  return <button type="button" onClick={scrollToOffer} className="w-full rounded-full bg-terra px-5 py-4 text-lg font-extrabold uppercase tracking-wide text-white shadow-[0_6px_0_#b77a1f] transition hover:brightness-105 active:translate-y-[3px] active:shadow-[0_3px_0_#b77a1f] sm:text-xl" data-id="element-1" data-mp="3ikqzm1my986s">
      {text} ↓
    </button>;
}
function VolumeItem({
  v,
  open,
  onToggle
}: {
  v: Volume;
  open: boolean;
  onToggle: () => void;
}) {
  return <div className="card overflow-hidden" data-id="element-2" data-mp="2zmo4d53t6f0j">
      <button type="button" onClick={onToggle} className="flex w-full items-center gap-3 p-4 text-left" aria-expanded={open} data-id="element-3" data-mp="33morde4z3yj7">
        <span className="font-display flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-sage text-xl font-extrabold text-cream" data-id="element-4" data-mp="38li77d7qg1mj">{v.num}</span>
        <span className="min-w-0 flex-1" data-id="element-5" data-mp="3dii5h1ycc033">
          <span className="block text-[10px] font-bold tracking-[0.15em] text-muted" data-id="element-6" data-mp="nwc582htoe10">
            CATEGORY {v.num} · {v.templates.length} TEMPLATES · {v.range.toUpperCase()}
          </span>
          <span className="font-display block text-lg font-bold leading-tight sm:text-xl" data-id="element-7" data-mp="35oz0whm5evr">{v.title}</span>
          <span className="block text-xs italic text-ink/70" data-id="element-8" data-mp="3udkr489qbgg7">“{v.pack}”</span>
        </span>
        <span className={`shrink-0 text-2xl font-bold transition ${open ? 'rotate-45 text-terra' : 'text-sage'}`} data-id="element-9" data-mp="d4xphfd9sbdd">+</span>
      </button>
      {open && <div className="border-t border-line bg-creamy/60 p-4" data-id="element-10" data-mp="86ckz3qzj5j4">
          <p className="text-sm text-ink/85" data-id="element-11" data-mp="3gmfwjkl7xecq">{v.summary}</p>
          <ol className="mt-3 grid gap-x-4 gap-y-1.5 sm:grid-cols-2" data-id="element-12" data-mp="3bmbf9schkflg">
            {v.templates.map(([id, title]) => <li key={id} className="flex min-w-0 gap-2 text-sm" data-id="element-13" data-mp="3qaxrhl6tgr8g">
                <span className="shrink-0 text-[12px] font-bold text-terra" data-id="element-14" data-mp="3qcipxlw94ubl">{id}</span>
                <span className="min-w-0 break-words" data-id="element-15" data-mp="3liwm8j82g0rx">{title}</span>
              </li>)}
          </ol>
          <p className="mt-3 text-xs font-semibold text-sage" data-id="element-16" data-mp="2xtbw9gdhw395">Every template in editable Word + PDF</p>
        </div>}
    </div>;
}
function FaqItem({
  q,
  a
}: {
  q: string;
  a: string;
}) {
  const [open, setOpen] = useState(false);
  return <div className="border-b border-line" data-id="element-17" data-mp="1d4hqpepkdsb5">
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-start justify-between gap-3 py-4 text-left" aria-expanded={open} data-id="element-18" data-mp="1d59linbc7m0d">
        <span className="font-semibold" data-id="element-19" data-mp="13eof4dpf9d6w">{q}</span>
        <span className={`shrink-0 text-xl font-bold transition ${open ? 'rotate-45 text-terra' : 'text-sage'}`} data-id="element-20" data-mp="13f7vhpzfbtit">+</span>
      </button>
      {open && <p className="pb-4 text-sm leading-relaxed text-ink/85" data-id="element-21" data-mp="1w3c16bhzw6li">{a}</p>}
    </div>;
}

// Mobile-only sticky bar: scrolls to #offer, hidden near the top and while the offer is visible.
function StickyBar() {
  const [show, setShow] = useState(false);
  const offerVisible = useRef(false);
  const scrolled = useRef(false);
  useEffect(() => {
    const update = () => setShow(scrolled.current && !offerVisible.current);
    const onScroll = () => {
      scrolled.current = window.scrollY > 520;
      update();
    };
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    const el = document.getElementById('offer');
    let io: IntersectionObserver | null = null;
    if (el && 'IntersectionObserver' in window) {
      io = new IntersectionObserver(entries => {
        offerVisible.current = entries[0].isIntersecting;
        update();
      }, {
        threshold: 0.05
      });
      io.observe(el);
    }
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      io?.disconnect();
    };
  }, []);
  return <div className={`fixed inset-x-0 bottom-0 z-40 border-t border-line bg-white/95 px-3 py-2 backdrop-blur transition-transform duration-300 md:hidden ${show ? 'translate-y-0' : 'translate-y-full'}`} data-id="element-22" data-mp="14xq2n4jpn28q">
      <div className="flex items-center gap-3" data-id="element-23" data-mp="vybbs3zmspnp">
        <div className="min-w-0 flex-1 leading-tight" data-id="element-24" data-mp="3kob56i270a6v">
          <p className="truncate text-sm font-extrabold uppercase" data-id="element-25" data-mp="3klq07k6lle4w">{TEMPLATE_COUNT} Templates · Word + PDF</p>
          <p className="text-xs" data-id="element-26" data-mp="1w4f2wmdkl9mj">
            <span className="text-muted line-through" data-id="element-27" data-mp="1w582n3e42yv6">${ORIGINAL_PRICE}</span> <span className="font-bold text-terra" data-id="element-28" data-mp="2060c9xqwb5rz">${PRICE}</span>
          </p>
        </div>
        <button type="button" onClick={scrollToOffer} className="shrink-0 rounded-full bg-terra px-4 py-2 text-sm font-bold uppercase text-white" data-id="element-29" data-mp="203ntjidbcrdv">
          See offer ↓
        </button>
      </div>
    </div>;
}
export function App() {
  const [openVol, setOpenVol] = useState<number>(0);
  return <div className="min-h-screen w-full overflow-x-hidden bg-[#fbfcfd]" data-id="element-30" data-mp="2ex0xl3cqqu5j">
      <Seo data-id="element-31" data-mp="2jsg75j3pu3w5" />
      <MetaPixel data-id="element-32" data-mp="2js6h5vio7wqg" />

      <header className="border-b border-line bg-white" data-id="element-33" data-mp="2jvu6eih31wzd">
        
      </header>

      {/* 1. HERO */}
      <section className="bg-sagegrad text-white" data-id="element-34" data-mp="2supdrxvv5d49">
        <div className="mx-auto grid max-w-5xl items-center gap-8 px-4 py-10 md:grid-cols-2 md:py-16" style={{
        paddingTop: "11px",
        paddingBottom: "12px",
        gap: "0px"
      }} data-id="element-35" data-mp="2steeobdd4tjh">
          <div className="min-w-0" data-id="element-36" data-mp="yg51o9llypwh">
            <span className="pill bg-white/15 text-cream" data-id="element-37" data-mp="yh6kj08v8gor">FOR THERAPISTS, COUNSELORS, LCSWS, MFTS & PSYCHOLOGISTS</span>
            <h1 className="font-display mt-4 text-[40px] font-extrabold leading-[1.02] sm:text-6xl" data-id="element-38" data-mp="21tojq66hn39e">
              Stop building paperwork from scratch.
              <br data-id="element-39" data-mp="2atbjyonhe30v" />
              <span className="text-terra" data-id="element-40" data-mp="2attqce3uxxmi">Get back to your clients.</span>
            </h1>
            <p className="mt-4 text-base leading-relaxed text-white/90 sm:text-lg" data-id="element-41" data-mp="25se5am1ojr44">
              <strong className="text-white" data-id="element-42" data-mp="2kpekxr6ljvm8">{TEMPLATE_COUNT} done-for-you templates</strong> for your private practice, in <strong className="text-white" data-id="element-43" data-mp="2fqk4gvw3qj5a">editable Word + PDF</strong>: intake,
              consent, SOAP, DAP and BIRP notes, treatment plans, billing and client letters.
            </p>
            <ul className="mt-4 space-y-1.5 text-sm text-white/90" data-id="element-44" data-mp="2pi7pof22f3yb">
              
              
              
            </ul>
          </div>
          <div className="min-w-0" data-id="element-45" data-mp="33eti3aj5w0lc">
            <BundleMockup data-id="element-46" data-mp="2yicwamzfpybf" />
            <div className="mt-4" data-id="element-47" data-mp="2yj5u2ygf1dhf">
              <SeeButton text={`See the ${TEMPLATE_COUNT} templates now`} data-id="element-48" data-mp="2yg05dkbp8dm8" />
            </div>
            <p className="mt-3 text-center text-[12px] text-white/75" data-id="element-49" data-mp="3i1jv3968zhmr">Instant download · 7-day risk-free guarantee</p>
          </div>
        <li data-id="element-50" data-mp="385p6lxfcesc2">✔ 6 categories · {TEMPLATE_COUNT} templates</li><li data-id="element-51" data-mp="38574ik601jw8">✔ + 4 bonuses included free</li><li data-id="element-52" data-mp="3d4jpezdb8gw6">✔ In-person and telehealth ready</li></div>
      </section>

      {/* 2. SOUND FAMILIAR */}
      <section className="bg-creamy" data-id="element-53" data-mp="2zdeyclo0cwxg">
        <div className="mx-auto max-w-5xl px-4 py-14" data-id="element-54" data-mp="2zcda1fhzedxs">
          <Label data-id="element-55" data-mp="343vewen4kyo1">Sound familiar?</Label>
          <h2 className="font-display mt-2 text-3xl font-extrabold leading-tight sm:text-5xl" data-id="element-56" data-mp="344wwl1yncjeq">You became a therapist for your clients. Not for the paperwork.</h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-57" data-mp="2udskqb49peo4">
            {PAINS.map(p => <div key={p.title} className="card min-w-0 p-5" data-id="element-58" data-mp="2ge9c7h7jo1ba">
                <span className="pill bg-sage/10 text-sage" data-id="element-59" data-mp="2gesu4xx72ppb">{p.tag}</span>
                <h3 className="font-display mt-3 text-xl font-bold leading-tight" data-id="element-60" data-mp="2ketborflbh0l">{p.title}</h3>
                <p className="mt-1 text-sm text-ink/80" data-id="element-61" data-mp="30uc7u1qoo6la">{p.text}</p>
              </div>)}
          </div>
          <p className="mx-auto mt-8 max-w-2xl text-center text-lg" data-id="element-62" data-mp="2vxc3bv9hfbwa">
            You already know what good documentation looks like. This gives you the forms, written and ready, so your evenings go back to your life.
          </p>
        </div>
      </section>

      {/* 3. CATEGORIES */}
      <section className="bg-white" data-id="element-63" data-mp="2hwrgmo0g4vxi">
        <div className="mx-auto max-w-3xl px-4 py-14" data-id="element-64" data-mp="2hxu7ivxrqydf">
          <Label data-id="element-65" data-mp="2d5j8dvaurglx">6 categories · {TEMPLATE_COUNT} templates · Word + PDF</Label>
          <h2 className="font-display mt-2 text-3xl font-extrabold leading-tight sm:text-5xl" data-id="element-66" data-mp="286ge95weuusq">Every form your practice needs, from first call to discharge.</h2>
          <p className="mt-3 text-ink/80" data-id="element-67" data-mp="239g7esrqlo0k">Open each category to see every template included.</p>
          <div className="mt-8 space-y-4" data-id="element-68" data-mp="1y80iuqqcozdf">
            {VOLUMES.map((v, i) => <VolumeItem key={v.num} v={v} open={openVol === i} onToggle={() => setOpenVol(openVol === i ? -1 : i)} data-id="element-69" data-mp="1u6otmmbe2g5r" data-mpi={i} />)}
          </div>
        </div>
      </section>

      {/* 4. INSIDE EVERY TEMPLATE + PREVIEW */}
      <section className="bg-sagegrad text-white" data-id="element-70" data-mp="dpg8j782qw4g">
        <div className="mx-auto max-w-5xl px-4 py-14" data-id="element-71" data-mp="ipktpajxgf10">
          <Label light data-id="element-72" data-mp="iqvud1i2t6nw">Inside every template</Label>
          <h2 className="font-display mt-2 text-3xl font-extrabold leading-tight sm:text-5xl" data-id="element-73" data-mp="nofbqaa2lzk2">Professional, complete and ready to use.</h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-74" data-mp="3m7m7e0c2sjz3">
            {INSIDE.map((p, i) => <div key={p.title} className="min-w-0 rounded-2xl border border-white/20 bg-white/10 p-5" data-id="element-75" data-mp="3r65vo7e567um" data-mpi={i}>
                <p className="text-[11px] font-bold text-terra" data-id="element-76" data-mp="3w3pap9inyonm">{String(i + 1).padStart(2, '0')}</p>
                <h3 className="font-display mt-1 text-xl font-bold" data-id="element-77" data-mp="4ql2pw8xzo1o">{p.title}</h3>
                <p className="mt-1 text-sm text-white/85" data-id="element-78" data-mp="33es3c1fzqc9n">{p.text}</p>
              </div>)}
          </div>

          <div className="mt-10 min-w-0 rounded-2xl bg-white p-5 text-ink sm:p-7" data-id="element-79" data-mp="38duz0va2z64y">
            <span className="pill bg-terra/15 text-terra" data-id="element-80" data-mp="2f7wtjdrigeol">REAL TEMPLATE PREVIEW</span>
            <h3 className="font-display mt-3 text-2xl font-extrabold leading-tight text-sage" data-id="element-81" data-mp="2k5yek48hyo2p">{PREVIEW.title}</h3>
            <p className="mt-3 text-sm" data-id="element-82" data-mp="2p51j7nkkesa5"><strong data-id="element-83" data-mp="2p5uglxxmw5fm">What it is:</strong> {PREVIEW.goal}</p>
            <p className="mt-1 text-sm" data-id="element-84" data-mp="2u4xn7mt9ijpp"><strong data-id="element-85" data-mp="2u32zy1uiqfig">Best for:</strong> {PREVIEW.bestFor}</p>
            <p className="mt-1 text-sm text-muted" data-id="element-86" data-mp="1wgvvqt24mp6m">{PREVIEW.time}</p>
            <p className="mt-4 text-sm font-bold" data-id="element-87" data-mp="1wej4tqee54hf">Sections</p>
            <ol className="mt-2 space-y-1.5" data-id="element-88" data-mp="21grzcr6wweln">
              {PREVIEW.steps.map((s, i) => <li key={s} className="flex min-w-0 gap-2 text-sm" data-id="element-89" data-mp="26ea0hveotpj3" data-mpi={i}>
                  <span className="shrink-0 font-bold text-terra" data-id="element-90" data-mp="26cywbuhbyfrg">{i + 1}.</span>
                  <span className="min-w-0 break-words" data-id="element-91" data-mp="2b6uqsmydxkhv">{s}</span>
                </li>)}
            </ol>
            <p className="mt-4 text-sm font-bold" data-id="element-92" data-mp="1dpw39hvfax8l">Also included</p>
            <ul className="mt-2 space-y-1" data-id="element-93" data-mp="1io7cbqdw93pr">
              {PREVIEW.questions.map(q => <li key={q} className="text-sm text-ink/85" data-id="element-94" data-mp="3vz8t6f2fbuj8">• {q}</li>)}
            </ul>
            <p className="mt-4 rounded-xl bg-creamy p-3 text-xs text-ink/80" data-id="element-95" data-mp="3rxx88muhvxhd">Templates only. Adapt to your state laws and licensing board requirements.</p>
          </div>
        </div>
      </section>

      {/* 5. BONUSES */}
      <section className="bg-creamy" data-id="element-96" data-mp="3i0isyqkb5eqn">
        <div className="mx-auto max-w-5xl px-4 py-14" data-id="element-97" data-mp="3hzhh4jvgwo7v">
          <Label data-id="element-98" data-mp="3d1698eoqgnsa">Included free · 4 bonuses</Label>
          <h2 className="font-display mt-2 text-3xl font-extrabold leading-tight sm:text-5xl" data-id="element-99" data-mp="3d1xuowom954u">Plus 4 bonuses for your practice.</h2>
          <div className="mt-8 grid gap-5 sm:grid-cols-2" data-id="element-100" data-mp="3482y4d1k8n0t">
            {BONUSES.map((b, i) => <div key={b.title} className="card flex min-w-0 gap-4 p-4" data-id="element-101" data-mp="345yiixuatzkp" data-mpi={i}>
                <div className="w-[38%] max-w-[150px] shrink-0" data-id="element-102" data-mp="2zb2qvysz5e84">
                  <BonusCover index={i} image={BONUS_IMAGES[i] || ''} short={b.short} data-id="element-103" data-mp="2uaesut6bvx4e" />
                </div>
                <div className="min-w-0 flex-1" data-id="element-104" data-mp="2pc3j0ig19mm6">
                  <p className="text-[11px] font-bold text-terra" data-id="element-105" data-mp="2pdem0g1nmqc8">BONUS {i + 1}</p>
                  <h3 className="font-display text-lg font-bold leading-tight sm:text-xl" data-id="element-106" data-mp="2fpj0e7qlq66j">{b.title}</h3>
                  <p className="mt-1 text-sm text-ink/80" data-id="element-107" data-mp="2kkga7gvpgjkh">{b.text}</p>
                  <p className="mt-2 text-sm" data-id="element-108" data-mp="26o2y6n9xxade">
                    <span className="text-muted line-through" data-id="element-109" data-mp="2aod3dxpwrwr7">${b.value}</span> <span className="font-bold text-go" data-id="element-110" data-mp="2aov8ljujtlb9">FREE</span>
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* 6. HOW IT WORKS */}
      <section className="bg-white" data-id="element-111" data-mp="33c90v4naxsp1">
        <div className="mx-auto max-w-5xl px-4 py-14" data-id="element-112" data-mp="2plvsx2aeudzp">
          <Label data-id="element-113" data-mp="2pl46oi2pdsm3">How it works</Label>
          <h2 className="font-display mt-2 text-3xl font-extrabold leading-tight sm:text-5xl" data-id="element-114" data-mp="2tn7k3c8xn7a0">Download. Personalize. Use it today.</h2>
          <div className="mt-8 grid gap-4 md:grid-cols-3" data-id="element-115" data-mp="3hbqupyfgfw59">
            {STEPS.map(s => <div key={s.n} className="card min-w-0 p-5" data-id="element-116" data-mp="3m3a07tsc3q94">
                <span className="font-display text-5xl font-extrabold leading-none text-terra" data-id="element-117" data-mp="3m4thnnpq87ch">{s.n}</span>
                <h3 className="font-display mt-3 text-xl font-bold" data-id="element-118" data-mp="1amgf56t01f9i">{s.title}</h3>
                <p className="mt-1 text-sm text-ink/80" data-id="element-119" data-mp="15p6ib63a61bn">{s.text}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 7. OFFER — the ONLY checkout button lives here */}
      <section id="offer" className="bg-sagegrad scroll-mt-4 text-white" data-id="element-120" data-mp="1ffrrfngkyo8t">
        <div className="mx-auto max-w-xl px-4 py-14" data-id="element-121" data-mp="rovts9pg0ytn">
          <div className="text-center" data-id="element-122" data-mp="rri8ctar4eld">
            <Label light data-id="element-123" data-mp="mrdt16lkxpwq">The offer</Label>
            <h2 className="font-display mt-2 text-3xl font-extrabold leading-tight sm:text-5xl" data-id="element-124" data-mp="msougzrjylko">Everything you get</h2>
          </div>
          <div className="mt-6 overflow-hidden rounded-2xl bg-white text-ink shadow-2xl" data-id="element-125" data-mp="wp1knnnw2hao">
            <div className="p-3" data-id="element-126" data-mp="2d304qv6fgqzq">
              <BundleMockup compact data-id="element-127" data-mp="2d1yugsx8k8j2" />
            </div>
            <div className="p-4 pt-0 sm:p-6 sm:pt-0" data-id="element-128" data-mp="2d4upo9hp8j3t">
              <ul className="divide-y divide-line text-sm" data-id="element-129" data-mp="2835g6ya4kdhu">
                <li className="flex items-center justify-between gap-3 py-3" data-id="element-130" data-mp="hn36b5qqfjjg">
                  <span className="min-w-0 font-semibold" data-id="element-131" data-mp="hp66gillmt1w">📘 120+ Done-For-You Templates for Therapists™ · {TEMPLATE_COUNT} templates, Word + PDF</span>
                  <span className="shrink-0 text-muted line-through" data-id="element-132" data-mp="qmq8vuv76ve3">${ORIGINAL_PRICE}</span>
                </li>
                {BONUSES.map(b => <li key={b.title} className="flex items-center justify-between gap-3 py-3" data-id="element-133" data-mp="vkhyujca87fn">
                    <span className="flex min-w-0 items-start gap-2" data-id="element-134" data-mp="10lfjd2krldkb">
                      <span className="shrink-0 text-base leading-none" data-id="element-135" data-mp="15jhb0bw9al7e">🎁</span>
                      <span className="min-w-0" data-id="element-136" data-mp="1ab8qnrdvj6ir">{b.title}</span>
                    </span>
                    <span className="shrink-0 whitespace-nowrap text-right" data-id="element-137" data-mp="1edlwsgwj96g2">
                      <span className="text-muted line-through" data-id="element-138" data-mp="1jc6ykkrfny6m">${b.value}</span> <span className="font-bold text-go" data-id="element-139" data-mp="1jdqe2959m57c">FREE</span>
                    </span>
                  </li>)}
                <li className="flex items-center gap-2 py-3" data-id="element-140" data-mp="1o95qcwnmjt1n">
                  <span className="shrink-0 font-bold text-go" data-id="element-141" data-mp="1yg8a9fosd7ix">✔</span>
                  <span className="font-semibold" data-id="element-142" data-mp="1tgdk5ecu4lyx">7-Day Risk-Free Guarantee</span>
                </li>
                <li className="flex items-center gap-2 py-3" data-id="element-143" data-mp="1ogrb7h41hrvx">
                  <span className="shrink-0 font-bold text-go" data-id="element-144" data-mp="1oj3nbdn49812">✔</span>
                  <span className="font-semibold" data-id="element-145" data-mp="1kgqshk9wquj2">Lifetime Access &amp; Free Updates</span>
                </li>
              </ul>
              <div className="mt-4 border-t border-line pt-4 text-center" data-id="element-146" data-mp="2h7splfzwrjee">
                <p className="text-sm" data-id="element-147" data-mp="2c75397mmjqqi">
                  Total value: <span className="text-muted line-through" data-id="element-148" data-mp="27fn2ajklpq5j">${TOTAL_VALUE}</span>
                </p>
                <p className="mt-1 text-sm" data-id="element-149" data-mp="23ejos3q1067h">
                  Regular price <span className="line-through" data-id="element-150" data-mp="23e1j6ghh4fmw">${ORIGINAL_PRICE}</span> · your price, one-time payment:
                </p>
                <p className="font-display text-7xl font-extrabold leading-none text-sage" data-id="element-151" data-mp="2v5hecyx3jaoq">${PRICE}</p>
                <p className="mt-3 text-xs text-muted" data-id="element-152" data-mp="t7xgwzftkluy">· Payment will be processed in your local currency ·</p>
                <button type="button" onClick={goToCheckout} className="mt-3 w-full rounded-full bg-go px-4 py-4 text-lg font-extrabold uppercase tracking-wide text-white shadow-[0_5px_0_#248a16] transition hover:brightness-105 active:translate-y-[3px] active:shadow-[0_2px_0_#248a16] sm:text-xl" data-id="element-153" data-mp="y7jr100cplzj">
                  Get the {TEMPLATE_COUNT} templates now
                </button>
                <p className="mt-3 text-[11px] text-muted" data-id="element-154" data-mp="fgiu0hisk0im">Secure checkout · Instant download</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 8. GUARANTEE */}
      <section className="bg-creamy" data-id="element-155" data-mp="5icw0b03vej1">
        <div className="mx-auto max-w-3xl px-4 py-14" data-id="element-156" data-mp="5je4q8tnfoxl">
          <div className="card flex flex-col items-center gap-5 p-6 text-center sm:flex-row sm:text-left" data-id="element-157" data-mp="1ux9ixxhcjpp3">
            <div className="font-display flex h-20 w-20 shrink-0 items-center justify-center rounded-full bg-sage text-3xl font-extrabold text-cream" data-id="element-158" data-mp="1zw40kw3kv87m">7</div>
            <div className="min-w-0" data-id="element-159" data-mp="3keo9qca4b5f0">
              <h2 className="font-display text-3xl font-extrabold leading-tight" data-id="element-160" data-mp="3fflflmvoejlt">7-Day Risk-Free Guarantee</h2>
              <p className="mt-2 text-ink/85" data-id="element-161" data-mp="3u4pzd8i4cvw1">
                Open the kit and use the templates with your next clients. If it does not fit your practice, email us within 7 days of purchase and you get your money back.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 9. FAQ */}
      <section className="bg-white" data-id="element-162" data-mp="2wvbt40v0nuh">
        <div className="mx-auto max-w-3xl px-4 py-14" data-id="element-163" data-mp="2zznemgnmlv6">
          <Label data-id="element-164" data-mp="gxxwpdg4bs3t">Questions</Label>
          <h2 className="font-display mt-2 text-3xl font-extrabold leading-tight sm:text-5xl" data-id="element-165" data-mp="gwwm1a30l7mm">Frequently asked questions</h2>
          <div className="mt-6 border-t border-line" data-id="element-166" data-mp="2ip1b8dt4pqtn">
            {FAQ.map(f => <FaqItem key={f.q} q={f.q} a={f.a} data-id="element-167" data-mp="2ipb2s5tto61g" />)}
          </div>
        </div>
      </section>

      {/* 10. FINAL CTA (scrolls to the offer) */}
      <section className="bg-sagegrad text-white" data-id="element-168" data-mp="1rx25xpsyr5k0">
        <div className="mx-auto max-w-2xl px-4 py-14 text-center" data-id="element-169" data-mp="1rwipkdiyop83">
          <h2 className="font-display text-3xl font-extrabold leading-tight sm:text-5xl" data-id="element-170" data-mp="1wv3ugq95rx2v">
            Less time on paperwork.
            <br data-id="element-171" data-mp="21n3x4qgm5435" />
            <span className="text-terra" data-id="element-172" data-mp="21ooq4bna4eyw">More time for your clients.</span>
          </h2>
          <p className="mx-auto mt-4 max-w-lg text-white/85" data-id="element-173" data-mp="26kwzt9ipjfyo">{TEMPLATE_COUNT} templates in Word + PDF and 4 bonuses, ready for your next client.</p>
          <div className="mx-auto mt-6 max-w-md" data-id="element-174" data-mp="1dwhq92ichpuq">
            <SeeButton text={`See the ${TEMPLATE_COUNT} templates now`} data-id="element-175" data-mp="1dx9lgc815lkh" />
          </div>
        </div>
      </section>

      {/* 11. FOOTER */}
      <footer className="border-t border-line bg-white pb-24 md:pb-10" data-id="element-176" data-mp="1nvqf5gycsea8">
        <div className="mx-auto max-w-3xl px-4 pt-10 text-center" data-id="element-177" data-mp="1nsuj5y62fzof">
          <p className="font-display text-2xl font-extrabold tracking-wide text-sage" data-id="element-178" data-mp="2uarc9pz472ie">
            SIGMA SMART<span className="text-terra" data-id="element-179" data-mp="2z8sxohk0jdx1">™</span>
          </p>
          <div className="mx-auto mt-6 max-w-md" data-id="element-180" data-mp="nnikwiucazc6">
            <BundleMockup compact data-id="element-181" data-mp="nm7m6xfr4hrx" />
          </div>
          <p className="mt-6 text-sm" data-id="element-182" data-mp="innushie2ds6">
            📧{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="break-all font-semibold underline" data-id="element-183" data-mp="iqa2qi96yvax">
              {SUPPORT_EMAIL}
            </a>
          </p>
          <p className="mt-2 text-sm" data-id="element-184" data-mp="8x32fbtb65hp">
            📲{' '}
            <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="font-semibold underline" data-id="element-185" data-mp="3z1each0zfzd">
              {INSTAGRAM_HANDLE}
            </a>
          </p>
          <p className="mx-auto mt-6 max-w-2xl text-xs leading-relaxed text-muted" data-id="element-186" data-mp="3w4wyov2de7us">
            Disclaimer: this is a digital kit of document templates for licensed mental health professionals. Templates only: they are not legal, billing or clinical advice
            and do not replace your professional judgment. Requirements vary by state, payer and licensing board; review and adapt every template to your state laws and
            licensing board requirements before use.
          </p>
          <p className="mt-4 text-[11px] text-muted" data-id="element-187" data-mp="2c9f7qymfs3fp">© {new Date().getFullYear()} SIGMA SMART™ · All rights reserved.</p>
        </div>
      </footer>

      {/* 12. MOBILE STICKY BAR (scrolls to the offer only) */}
      <StickyBar data-id="element-188" data-mp="22f6iqkwm6sma" />
    </div>;
}